import React from "react";
import { Check, Minus, HelpCircle, KeyRound, UserRoundX } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { UserAvatar } from "@/components/common";

const TIP = "max-w-[280px] rounded-lg border-black/[0.08] bg-white px-2.5 py-1.5 text-[11.5px] leading-snug text-foreground shadow-float";

/** One decision: yes (code allows), no (code denies, with the reason) or unknown (not decided by this system). */
export function AccessMark({ v, cap, testId, withLabel = false }) {
  const state = v?.allowed === true ? "ja" : v?.allowed === false ? "nei" : "ukjent";
  const icon = state === "ja"
    ? <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--v-ok-bg))] text-[hsl(var(--v-ok))]"><Check className="h-3 w-3" strokeWidth={3} /></span>
    : state === "nei"
      ? <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-black/[0.05] text-muted-foreground/70"><Minus className="h-3 w-3" strokeWidth={2.5} /></span>
      : <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-dashed border-[hsl(var(--v-assumed))] text-[hsl(var(--v-assumed))]"><HelpCircle className="h-3 w-3" strokeWidth={2.2} /></span>;
  const text = state === "ja" ? "Ja" : state === "nei" ? "Nei" : "Ukjent";
  const tip = `${cap.label}: ${text}${v?.reason ? ` – ${v.reason}` : ""}`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn("inline-flex items-center gap-1.5 outline-none", withLabel && "text-[12.5px]")} tabIndex={0} aria-label={tip} data-testid={testId} data-state={state}>{icon}{withLabel ? <span className={cn(state === "ukjent" && "text-[hsl(var(--v-assumed))]", state === "nei" && "text-muted-foreground")}>{text}</span> : null}</span>
      </TooltipTrigger>
      <TooltipContent side="top" className={TIP}>{tip}</TooltipContent>
    </Tooltip>
  );
}

export function PersonCell({ p, me }) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <UserAvatar name={p.name} initials={p.initials} color={p.color} src={p.avatar_url} size="md" className="h-8 w-8 shrink-0" />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-1.5 text-[13px] font-medium leading-tight">{p.name}{p.id === me ? <span className="text-[11px] font-normal text-muted-foreground">(deg)</span> : null}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11.5px] text-muted-foreground">
          {p.login
            ? <span className="inline-flex items-center gap-1" title="Har innlogging"><KeyRound className="h-3 w-3" />{p.role_label || (p.role ? <span className="text-[hsl(var(--v-assumed))]">{p.role} · ukjent rolle</span> : <span className="text-[hsl(var(--v-assumed))]">ingen rolle</span>)}</span>
            : <span className="inline-flex items-center gap-1" title="Uten innlogging"><UserRoundX className="h-3 w-3" /> uten innlogging</span>}
          {p.title ? <span>· {p.title}</span> : null}
          {p.dept_label ? <span>· {p.dept_label}</span> : null}
          {p.login && !p.is_active ? <span className="text-[hsl(var(--v-blocked))]">· deaktivert</span> : null}
        </div>
      </div>
    </div>
  );
}

/** Table (≥768px) or stacked cards: people × capabilities, every cell decided by the server's gate code. */
export function AccessMatrix({ people, capabilities, me, mobile }) {
  if (mobile) {
    return (
      <div className="divide-y divide-black/[0.05]" data-testid="access-matrix" data-view="cards">
        {people.map((p) => (
          <div key={p.id} className="px-4 py-3" data-testid={`access-row-${p.id}`} data-login={p.login ? "1" : "0"}>
            <PersonCell p={p} me={me} />
            <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
              {capabilities.map((c) => <li key={c.key} className="flex items-center justify-between gap-2 text-[12px]"><span className="truncate text-muted-foreground">{c.short}</span><AccessMark v={p.access[c.key]} cap={c} testId={`access-cell-${p.id}-${c.key}`} /></li>)}
            </ul>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto scrollbar-thin" data-testid="access-matrix" data-view="table">
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr className="text-left text-[11px] font-medium text-muted-foreground">
            <th className="px-4 pb-2 pt-3 font-medium">Person</th>
            {capabilities.map((c) => (
              <th key={c.key} className="px-2 pb-2 pt-3 text-center font-medium" data-testid={`access-col-${c.key}`}>
                <Tooltip><TooltipTrigger asChild><span className="inline-block cursor-help whitespace-nowrap leading-tight underline decoration-dotted decoration-black/20 underline-offset-2" tabIndex={0}>{c.short}</span></TooltipTrigger><TooltipContent side="top" className={TIP}><div className="font-medium">{c.label}</div><div className="mt-0.5 text-muted-foreground">{c.rule}</div></TooltipContent></Tooltip>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-black/[0.05]">
          {people.map((p) => (
            <tr key={p.id} className="hover:bg-[hsl(210_20%_98.5%)]" data-testid={`access-row-${p.id}`} data-login={p.login ? "1" : "0"}>
              <td className="px-4 py-2.5"><PersonCell p={p} me={me} /></td>
              {capabilities.map((c) => <td key={c.key} className="px-2 py-2.5 text-center"><AccessMark v={p.access[c.key]} cap={c} testId={`access-cell-${p.id}-${c.key}`} /></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
